import { useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Loader2, Inbox, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { STAGE_LABEL } from "../lib/pipeline";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "./DeleteWithReasonDialog";

const MIN_SEARCH_LENGTH = 2;

function ProspectRow({ prospect, busy, onAttach }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-t border-slate-100 first:border-t-0">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{prospect.pharmacy_name}</div>
        <div className="text-xs text-slate-500 truncate">
          {[prospect.commune, prospect.phone, prospect.email].filter(Boolean).join(" · ") || "Aucune coordonnée"}
          {prospect.status && <> · {STAGE_LABEL[prospect.status] || prospect.status}</>}
        </div>
      </div>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onAttach(prospect.id)}>
        Rattacher
      </Button>
    </div>
  );
}

function ReviewDialog({ item, onClose, onResolved }) {
  const { submission, candidates } = item;
  const answers = submission.answers || {};
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const canDelete = hasPermission(user, "google_sheet:delete_submission");
  const [deleting, setDeleting] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/prospects", { params: { search: trimmed } });
        setSearchResults(data.filter((p) => !candidates.some((c) => c.id === p.id)));
      } catch (e) {
        // best-effort search: keep last results rather than surfacing an error
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm, candidates]);

  const attach = async (prospectId) => {
    setBusy(true);
    try {
      await api.post(`/form-submissions/${submission.id}/attach`, { prospect_id: prospectId });
      toast.success("Soumission rattachée au prospect");
      onResolved();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de rattacher cette soumission"));
      setBusy(false);
    }
  };

  const createProspect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/form-submissions/${submission.id}/create-prospect`);
      toast.success(`Prospect "${data.prospect.pharmacy_name}" créé`);
      onResolved();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de créer le prospect"));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Traiter la soumission</DialogTitle>
        </DialogHeader>

        <div className="text-sm space-y-1 rounded-md bg-slate-50 border border-slate-200 p-3">
          <div className="font-medium text-slate-900">{answers.pharmacy_name || "Pharmacie sans nom"}</div>
          <div className="text-xs text-slate-500">
            {[answers.commune, answers.phone, answers.email].filter(Boolean).join(" · ") || "Aucune coordonnée renseignée"}
          </div>
          <div className="text-xs text-slate-400">
            Reçue le {new Date(submission.submitted_at).toLocaleString("fr-FR")}
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
            {candidates.length > 0 ? "Correspondances possibles" : "Aucune correspondance automatique"}
          </div>
          {candidates.map((p) => (
            <ProspectRow key={p.id} prospect={p} busy={busy} onAttach={attach} />
          ))}
        </div>

        <div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">
            Ou rechercher un autre prospect
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nom de la pharmacie, téléphone, email…"
              className="pl-8"
            />
          </div>
          {searching && <div className="text-xs text-slate-400 mt-1">Recherche…</div>}
          {searchResults.map((p) => (
            <ProspectRow key={p.id} prospect={p} busy={busy} onAttach={attach} />
          ))}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div className="flex items-center gap-1">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
            {canDelete && (
              <Button variant="ghost" className="text-red-700 hover:text-red-800" onClick={() => setDeleting(submission)} disabled={busy} data-testid="submission-delete">
                <Trash2 size={14} className="mr-1.5" /> Supprimer…
              </Button>
            )}
          </div>
          <Button variant="outline" onClick={createProspect} disabled={busy}>
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Créer un nouveau prospect
          </Button>
        </DialogFooter>
      </DialogContent>
      <DeleteWithReasonDialog
        target={deleting} testId="delete-submission" onClose={() => setDeleting(null)}
        title="Supprimer cette réponse du formulaire ?"
        description="Elle va dans la corbeille et se restaure pendant 30 jours. Elle ne reviendra pas à la prochaine synchronisation."
        onConfirm={async (reason) => {
          setDeleting(null);
          setBusy(true);
          try {
            await api.delete(`/form-submissions/${submission.id}`, { data: { reason } });
            toast.success("Réponse mise à la corbeille");
            onResolved();
          } catch (e) {
            toast.error(errorMessage(e, "Impossible de supprimer cette réponse"));
            setBusy(false);
          }
        }}
      />
    </Dialog>
  );
}

export default function UnassignedSubmissions({ refreshKey }) {
  const [items, setItems] = useState(null);
  const [reviewing, setReviewing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/form-submissions/unassigned");
      setItems(data);
    } catch (e) {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, [refreshKey]);

  if (items === null) {
    return (
      <Card className="p-5 flex justify-center">
        <Loader2 className="animate-spin text-jamora" size={18} />
      </Card>
    );
  }

  return (
    <Card className="p-5" data-testid="unassigned-submissions">
      <div className="flex items-center justify-between mb-1">
        <div className="font-display font-semibold text-sm text-slate-900">Soumissions à traiter</div>
        {items.length > 0 && (
          <Badge variant="outline" className="border-transparent bg-amber-50 text-amber-700 text-2xs font-medium">
            {items.length}
          </Badge>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Réponses au formulaire que la synchronisation n'a pas pu rattacher automatiquement à un prospect.
      </p>

      {items.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
          <Inbox size={16} className="text-slate-400" />
          Aucune soumission en attente de traitement.
        </div>
      ) : (
        items.map((item) => (
          <div key={item.submission.id} className="flex items-center justify-between gap-3 py-2 border-t border-slate-100 first:border-t-0">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">
                {item.submission.answers?.pharmacy_name || "Sans nom"}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {new Date(item.submission.submitted_at).toLocaleString("fr-FR")}
                {item.candidates.length > 0 && <> · {item.candidates.length} correspondance(s) possible(s)</>}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setReviewing(item)}>Traiter</Button>
          </div>
        ))
      )}

      {reviewing && (
        <ReviewDialog
          item={reviewing}
          onClose={() => setReviewing(null)}
          onResolved={() => { setReviewing(null); load(); }}
        />
      )}
    </Card>
  );
}
